import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Dropdown, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';

import axios, { apiBaseUrl } from '../api/httpClient';
import { safeEvidenceUrl } from '../api/controlledEvidence';
import { useAuth } from '../auth/AuthContext';
import { imsDate } from './integratedManagementTypes';
import IntegratedManagementControlDetails, { controlFramework } from './IntegratedManagementControlDetails';
import IntegratedManagementAssuranceFields, { Assurance, assuranceTypes, emptyAssurance, existingAssurance, IntegratedManagementAssuranceDetails } from './IntegratedManagementAssuranceFields';

interface EvidenceLink {
  url: string;
  title?: string | null;
  sha256?: string | null;
  version?: string | null;
}

interface OperationalRecord {
  id: string;
  type: string;
  name: string;
  status: string;
  owner: { name: string | null; email: string | null };
  standards: string[];
  reviewDueOn: string | null;
  effectiveFrom: string | null;
  source: { system: string; identifier: string | null };
  jiraKey: string | null;
  evidenceLinks: EvidenceLink[];
  attributes: Record<string, unknown>;
  review?: { outcome: string; rationale: string; reviewedVersion: number; completedOn: string; recordedAt: string } | null;
  approval?: { decision: string; rationale: string; reviewedVersion: number; recordedAt: string } | null;
  recordVersion: number;
  createdBy: string;
  updatedBy: string;
  createdAt: string | null;
  updatedAt: string | null;
  retiredAt: string | null;
  retiredBy: string | null;
}

interface RecordListResponse {
  page: number;
  perPage: number;
  total: number;
  lastPage: number;
  items: OperationalRecord[];
}

interface RecordVersion {
  version: number;
  action: string;
  actor: string;
  snapshotSha256: string;
  createdAt: string | null;
  snapshot?: OperationalRecord;
}

interface AuditEvent {
  id: string;
  action: string;
  actor: { identifier: string; name: string | null; email: string | null; kind: string };
  recordVersion: number;
  afterSha256: string;
  metadata: { changedFields?: string[] };
  createdAt: string | null;
}

interface RecordDraft {
  type: string;
  name: string;
  status: string;
  ownerName: string;
  ownerEmail: string;
  standards: string[];
  reviewDueOn: string;
  effectiveFrom: string;
  jiraKey: string;
  evidenceLinks: EvidenceLink[];
  description: string;
  implementation: string;
  expectedEvidence: string;
  requirementRefs: string;
  successCriteria: string;
  executionMode: string;
  policyBody: string;
  documentVersion: string;
  riskAssessment: {likelihood: number; consequence: number; residualLikelihood: number; residualConsequence: number; treatmentStrategy: string; treatmentExplanation: string};
  assurance: Assurance | null;
}

interface DecisionDraft {
  action: 'review' | 'approval';
  outcome: string;
  decision: string;
  rationale: string;
  nextReviewOn: string;
  onBehalfName: string;
  onBehalfEmail: string;
  delegationReason: string;
}

interface SourcePolicyVersion {
  sourceId: string; version: string; currentSourceVersion: boolean;
  clauseCount: number; updatedAt: string | null; sha256: string; evidenceUrl: string | null;
}

interface SourceFact {
  kind: string; rowCount: number; rowsWithDecisionTimestamp: number;
  evidenceUrl: string | null;
}

const recordTypes = [
  ['frameworks', 'Framework'], ['controlTestRuns', 'Control test run'], ['tasks', 'Task'],
  ['people', 'Person'], ['aiSystems', 'AI system'],
  ['requirements', 'Requirement'], ['controls', 'Control'], ['controlTests', 'Control test'],
  ['policies', 'Policy'], ['policyTemplates', 'Policy template catalogue'], ['policyDocuments', 'Policy document'], ['risks', 'Risk'],
  ['assets', 'Asset'], ['suppliers', 'Supplier'], ['objectives', 'Objective'],
  ['audits', 'Audit'], ['managementReviews', 'Management review'],
  ['nonconformities', 'Nonconformity'], ['correctiveActions', 'Corrective action'],
  ['services', 'Service'], ['incidents', 'Incident'], ['serviceRequests', 'Service request'],
  ['problems', 'Problem'], ['changes', 'Change'], ['releases', 'Release'],
  ['environmentalAspects', 'Environmental aspect'],
  ['complianceObligations', 'Compliance obligation'], ['competencies', 'Competence'],
  ['communications', 'Communication'],
] as const;

const statuses = [
  'draft', 'pending-review', 'active', 'open', 'in-progress',
  'implemented', 'verified', 'closed', 'superseded',
];

const emptyDraft = (): RecordDraft => ({
  type: 'risks', name: '', status: 'draft', ownerName: '', ownerEmail: '', standards: [],
  reviewDueOn: '', effectiveFrom: '', jiraKey: '', evidenceLinks: [], description: '', implementation: '', expectedEvidence: '', requirementRefs: '', successCriteria: '', executionMode: 'human', policyBody: '', documentVersion: '1.0', riskAssessment:{likelihood:1,consequence:1,residualLikelihood:1,residualConsequence:1,treatmentStrategy:'modify',treatmentExplanation:''}, assurance: null,
});

const draftFromRecord = (record: OperationalRecord): RecordDraft => ({
  type: record.type,
  name: record.name,
  status: ['approved', 'accepted', 'imported'].includes(record.status) || (assuranceTypes.includes(record.type) && ['verified', 'closed'].includes(record.status)) ? 'draft' : record.status,
  ownerName: record.owner?.name ?? '',
  ownerEmail: record.owner?.email ?? '',
  standards: record.standards ?? [],
  reviewDueOn: record.reviewDueOn ?? '',
  effectiveFrom: record.effectiveFrom ?? '',
  jiraKey: record.jiraKey ?? '',
  evidenceLinks: (record.evidenceLinks ?? []).map((link) => ({ ...link })),
  description: typeof record.attributes?.description === 'string' ? record.attributes.description : '',
  implementation: typeof record.attributes?.implementation === 'string' ? record.attributes.implementation : '',
  expectedEvidence: Array.isArray(record.attributes?.expectedEvidence) ? record.attributes.expectedEvidence.filter((item) => typeof item === 'string').join('\n') : '',
  requirementRefs: Array.isArray(record.attributes?.requirementRefs) ? record.attributes.requirementRefs.filter((item) => typeof item === 'string').join('\n') : '',
  successCriteria: typeof record.attributes?.successCriteria === 'string' ? record.attributes.successCriteria : '',
  executionMode: record.attributes?.executionMode === 'automated' ? 'automated' : 'human',
  policyBody: typeof record.attributes?.policyBody === 'string' ? record.attributes.policyBody : '',
  documentVersion: typeof record.attributes?.documentVersion === 'string' ? record.attributes.documentVersion : typeof record.attributes?.sourcePolicyVersion === 'string' ? record.attributes.sourcePolicyVersion : '1.0',
  riskAssessment: riskDraft(record.attributes),
  assurance: existingAssurance(record.attributes?.assurance),
});

function riskDraft(attributes: Record<string, unknown>): RecordDraft['riskAssessment'] {
  const native = (attributes.riskAssessment ?? {}) as Record<string,unknown>;
  const source = (attributes.adoptech ?? {}) as Record<string,unknown>;
  const rating = (key:string, legacy:string) => {const n=Number(native[key]??source[legacy]??1);return Number.isInteger(n)&&n>=1&&n<=5?n:1;};
  return {likelihood:rating('likelihood','inherent_risk_likelihood'),consequence:rating('consequence','inherent_risk_consequence'),residualLikelihood:rating('residualLikelihood','residual_risk_likelihood'),residualConsequence:rating('residualConsequence','residual_risk_consequence'),treatmentStrategy:String(native.treatmentStrategy??source.treatment_strategy??'modify'),treatmentExplanation:String(native.treatmentExplanation??source.treatment_explanation??'')};
}

const formatTimestamp = (value: string | null): string => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('en-GB');
};

const errorMessage = (error: unknown, fallback: string): string => {
  const responseMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return responseMessage || fallback;
};

const sourcePolicyVersions = (record: OperationalRecord | null): SourcePolicyVersion[] => {
  const value = record?.attributes.sourcePolicyVersions;
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is SourcePolicyVersion => item && typeof item === 'object' &&
    typeof item.sourceId === 'string' && typeof item.version === 'string' &&
    typeof item.currentSourceVersion === 'boolean' && typeof item.sha256 === 'string' &&
    typeof item.clauseCount === 'number' && Number.isFinite(item.clauseCount) && item.clauseCount >= 0);
};

const sourceFacts = (record: OperationalRecord | null): SourceFact[] => {
  const value = record?.attributes.sourceFacts;
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is SourceFact => item && typeof item === 'object' && typeof item.kind === 'string' &&
    Number.isInteger(item.rowCount) && item.rowCount >= 0 && Number.isInteger(item.rowsWithDecisionTimestamp) && item.rowsWithDecisionTimestamp >= 0 && item.rowsWithDecisionTimestamp <= item.rowCount);
};

const IntegratedManagementOperationalRecords: React.FC<{ standards: string[]; recordType?: string; title?: string; selectedStandard?: string; onStandardChange?: (standard: string) => void }> = ({ standards, recordType, title, selectedStandard, onStandardChange }) => {
  const focused = Boolean(recordType);
  const singular = recordTypes.find(([value]) => value === recordType)?.[1] || 'record';
  const pageSize = focused ? 25 : 100;
  const { hasPermission } = useAuth();
  const canManage = hasPermission('integrated-management-system.manage');
  const [records, setRecords] = useState<OperationalRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(recordType || '');
  const [includeRetired, setIncludeRetired] = useState(false);
  const [localStandard, setLocalStandard] = useState('');
  const standardFilter = selectedStandard ?? localStandard;
  const invalidStandard = Boolean(standardFilter && !standards.includes(standardFilter));
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<OperationalRecord | null>(null);
  const [draft, setDraft] = useState<RecordDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<OperationalRecord | null>(null);
  const [versions, setVersions] = useState<RecordVersion[]>([]);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [inspectedVersion, setInspectedVersion] = useState<RecordVersion | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const latestRequest = useRef(0);
  const [decisionRecord, setDecisionRecord] = useState<OperationalRecord | null>(null);
  const [decisionDraft, setDecisionDraft] = useState<DecisionDraft | null>(null);
  const [decisionError, setDecisionError] = useState('');
  const [publication, setPublication] = useState<{url: string; sha256: string} | null>(null);

  const loadRecords = useCallback(async () => {
    const requestNumber = ++latestRequest.current;
    setLoading(true);
    setError('');
    if (invalidStandard) {
      setRecords([]); setTotal(0); setLastPage(1); setLoading(false);
      setError('Unknown standard filter. Select an applicable standard; no unfiltered data has been substituted.');
      return;
    }
    try {
      const response = await axios.get<RecordListResponse>(
        `${apiBaseUrl}/integrated-management-system/operational-records`,
        {
          params: {
            perPage: pageSize,
            page,
            ...(appliedSearch ? { search: appliedSearch } : {}),
            ...(recordType || typeFilter ? { type: recordType || typeFilter } : {}),
            ...(includeRetired ? { includeRetired: 1 } : {}),
            ...(standardFilter ? { standard: standardFilter } : {}),
          },
        }
      );
      if (requestNumber === latestRequest.current) {
        setRecords(response.data.items);
        setTotal(response.data.total);
        setLastPage(response.data.lastPage);
      }
    } catch (loadError) {
      if (requestNumber === latestRequest.current) setError(errorMessage(loadError, 'Operational IMS records could not be loaded.'));
    } finally {
      if (requestNumber === latestRequest.current) setLoading(false);
    }
  }, [includeRetired, appliedSearch, typeFilter, page, recordType, pageSize, standardFilter, invalidStandard]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const openCreate = () => {
    setEditing(null);
    setDraft({ ...emptyDraft(), ...(recordType ? { type: recordType, assurance: emptyAssurance(recordType) } : {}), standards: standardFilter && !invalidStandard ? [standardFilter] : [] });
    setEditorOpen(true);
  };

  const openEdit = (record: OperationalRecord) => {
    setEditing(record);
    setDraft(draftFromRecord(record));
    setEditorOpen(true);
  };

  const toggleStandard = (standard: string) => {
    setDraft((current) => ({
      ...current,
      standards: current.standards.includes(standard)
        ? current.standards.filter((item) => item !== standard)
        : [...current.standards, standard],
    }));
  };

  const updateEvidence = (index: number, field: 'url' | 'title', value: string) => {
    setDraft((current) => ({
      ...current,
      evidenceLinks: current.evidenceLinks.map((link, position) => (
        position === index ? { ...link, [field]: value } : link
      )),
    }));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      ...(editing ? {} : { type: draft.type }),
      name: draft.name.trim(),
      status: draft.status,
      owner: draft.ownerName.trim() || draft.ownerEmail.trim()
        ? { name: draft.ownerName.trim() || null, email: draft.ownerEmail.trim() || null }
        : null,
      standards: draft.standards,
      reviewDueOn: draft.reviewDueOn || null,
      effectiveFrom: draft.effectiveFrom || null,
      jiraKey: draft.jiraKey.trim() || null,
      evidenceLinks: draft.evidenceLinks
        .filter((link) => link.url.trim())
        .map((link) => ({ ...link, url: link.url.trim(), title: link.title?.trim() || null })),
      attributes: { ...(editing?.attributes ?? {}), description: draft.description.trim(),
        ...(['controls', 'requirements'].includes(draft.type) ? { implementation: draft.implementation.trim(), expectedEvidence: draft.expectedEvidence.split('\n').map((line) => line.trim()).filter(Boolean), requirementRefs: draft.requirementRefs.split('\n').map((line) => line.trim()).filter(Boolean) } : {}),
        ...(draft.assurance ? { assurance: draft.assurance } : {}),
        ...(draft.type === 'policies' && draft.policyBody.trim() ? { policyBody: draft.policyBody, documentVersion: draft.documentVersion } : {}),
        ...(draft.type === 'risks' && draft.riskAssessment.treatmentExplanation.trim() ? {riskAssessment:draft.riskAssessment} : {}),
        ...(draft.type === 'controlTests' ? { successCriteria: draft.successCriteria.trim(), executionMode: draft.executionMode } : {}) },
    };

    try {
      if (editing) {
        await axios.patch(
          `${apiBaseUrl}/integrated-management-system/operational-records/${editing.id}`,
          payload,
          { headers: { 'If-Match': `"${editing.recordVersion}"` } }
        );
      } else {
        await axios.post(`${apiBaseUrl}/integrated-management-system/operational-records`, payload);
      }
      setEditorOpen(false);
      await loadRecords();
    } catch (saveError) {
      setError(errorMessage(saveError, 'The controlled IMS record could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const showHistory = async (record: OperationalRecord) => {
    setDetail(record);
    setInspectedVersion(null);
    setDetailLoading(true);
    setVersions([]);
    setEvents([]);
    setError('');
    try {
      const [versionResponse, auditResponse] = await Promise.all([
        axios.get<{ items: RecordVersion[] }>(`${apiBaseUrl}/integrated-management-system/operational-records/${record.id}/versions`),
        axios.get<{ items: AuditEvent[] }>(`${apiBaseUrl}/integrated-management-system/operational-records/${record.id}/audit-events`),
      ]);
      setVersions(versionResponse.data.items);
      setEvents(auditResponse.data.items);
    } catch (historyError) {
      setError(errorMessage(historyError, 'Version and audit history could not be loaded.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const extractSourceText = async () => {
    if (!editing || saving) return;
    setSaving(true); setError('');
    try {
      const response = await axios.get<{recordVersion: number; policyBody: string}>(`${apiBaseUrl}/integrated-management-system/operational-records/${editing.id}/policy-source-text`);
      if (response.data.recordVersion !== editing.recordVersion) throw new Error('Stale source');
      setDraft((current) => ({...current, policyBody: response.data.policyBody}));
    } catch (problem) {setError(errorMessage(problem,'Verified source text is unavailable or the record changed. Open the protected PDF and refresh the record.'));}
    finally {setSaving(false);}
  };
  const publishPolicy = async (record: OperationalRecord) => {
    if (saving) return;
    setSaving(true); setError('');
    try {
      const response = await axios.post<{url: string; sha256: string}>(`${apiBaseUrl}/integrated-management-system/operational-records/${record.id}/policy-publication`,{}, {headers: {'If-Match': `"${record.recordVersion}"`}});
      if (!safeEvidenceUrl(response.data.url)) throw new Error('Unsafe publication URL');
      setPublication({url: response.data.url, sha256: response.data.sha256});
    } catch (problem) {setError(errorMessage(problem,'Controlled publication could not be independently verified.'));}
    finally {setSaving(false);}
  };

  const openDecision = (record: OperationalRecord, action: 'review' | 'approval') => {
    const nextReview = new Date();
    nextReview.setMonth(nextReview.getMonth() + 6);
    setDecisionRecord(record);
    setDecisionError('');
    setDecisionDraft({
      action, outcome: 'no-change', decision: record.type === 'risks' ? 'accept-risk' : 'approve',
      rationale: '', nextReviewOn: nextReview.toISOString().slice(0, 10),
      onBehalfName: '', onBehalfEmail: '', delegationReason: '',
    });
  };

  const saveDecision = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!decisionRecord || !decisionDraft) return;
    setSaving(true);
    setDecisionError('');
    const delegation = decisionDraft.onBehalfName.trim() ? {
      onBehalfOf: { name: decisionDraft.onBehalfName.trim(), email: decisionDraft.onBehalfEmail.trim() || null },
      delegationReason: decisionDraft.delegationReason.trim(),
    } : {};
    const payload = {
      rationale: decisionDraft.rationale.trim(), ...delegation,
      ...(decisionDraft.action === 'review'
        ? { outcome: decisionDraft.outcome, nextReviewOn: decisionDraft.nextReviewOn }
        : { decision: decisionDraft.decision }),
    };
    try {
      await axios.post(
        `${apiBaseUrl}/integrated-management-system/operational-records/${decisionRecord.id}/${decisionDraft.action}`,
        payload, { headers: { 'If-Match': `"${decisionRecord.recordVersion}"` } }
      );
      setDecisionRecord(null);
      setDecisionDraft(null);
      await loadRecords();
    } catch (decisionFailure) {
      setDecisionError(errorMessage(decisionFailure, 'The review or approval could not be recorded.'));
    } finally {
      setSaving(false);
    }
  };

  const retire = async (record: OperationalRecord) => {
    if (!window.confirm(`Retire “${record.name}”? The full history will remain available.`)) return;
    setError('');
    try {
      await axios.delete(
        `${apiBaseUrl}/integrated-management-system/operational-records/${record.id}`,
        { headers: { 'If-Match': `"${record.recordVersion}"` } }
      );
      await loadRecords();
    } catch (retireError) {
      setError(errorMessage(retireError, 'The controlled IMS record could not be retired.'));
    }
  };

  const activeCount = useMemo(() => records.filter((record) => !record.retiredAt).length, [records]);

  return (
    <Card className={focused ? 'ims-focused-register' : 'mb-4'}>
      <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <strong>{title || 'Operational IMS records'}</strong>
          <div className="small text-muted">Versioned native records · {activeCount} shown · {total} matching</div>
        </div>
        {canManage && <Button onClick={openCreate}>{focused ? `New ${singular.toLowerCase()}` : 'Create controlled record'}</Button>}
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form className="d-flex flex-wrap gap-2 align-items-end" onSubmit={(event) => { event.preventDefault(); setPage(1); if (appliedSearch === search.trim() && page === 1) loadRecords(); else setAppliedSearch(search.trim()); }}>
          <Form.Group>
            <Form.Label>Standard</Form.Label>
            <Form.Select aria-label="Filter by standard" value={standardFilter} onChange={(event) => { setPage(1); if (onStandardChange) onStandardChange(event.target.value); else setLocalStandard(event.target.value); }}>
              <option value="">All standards / frameworks</option>
              {invalidStandard && <option value={standardFilter}>Unknown standard</option>}
              {standards.map((standard) => <option key={standard} value={standard}>{standard}</option>)}
            </Form.Select>
          </Form.Group>
          {!focused && <Form.Group>
            <Form.Label>Operational type</Form.Label>
            <Form.Select aria-label="Operational type" value={typeFilter} onChange={(event) => { setPage(1); setTypeFilter(event.target.value); }}>
              <option value="">All types</option>
              {recordTypes.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
            </Form.Select>
          </Form.Group>}
          <Form.Group className="flex-grow-1">
            <Form.Label>Search operational records</Form.Label>
            <Form.Control aria-label="Search operational records" placeholder={focused ? 'Search this register' : undefined} value={search} onChange={(event) => setSearch(event.target.value)} />
          </Form.Group>
          <Form.Check
            className="mb-2"
            type="checkbox"
            id="ims-include-retired"
            label="Include retired"
            checked={includeRetired}
            onChange={(event) => { setPage(1); setIncludeRetired(event.target.checked); }}
          />
          <Button variant="outline-primary" type="submit" disabled={loading}>Refresh</Button>
        </Form>
        {['controls', 'requirements'].includes(recordType || typeFilter) && <p className="small text-muted mt-3 mb-0">Select a standard to see its mappings. ISO 27001 Annex A controls are separate from Cyber Essentials. Draft preparation and imported history are not verified conformity.</p>}
      </Card.Body>
      <div className="border-top">
        {loading ? (
          <div className="p-4 text-center"><Spinner animation="border" size="sm" className="me-2" />Loading operational records…</div>
        ) : (
          <Table responsive hover className={`mb-0 align-middle${focused ? ' ims-register-table' : ''}`}>
            <thead><tr><th>Record</th>{!focused && <th>Type</th>}<th>Status</th><th>Owner</th><th>Review due</th><th>Version</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  <td>{focused ? <Button variant="link" className="ims-record-title" onClick={() => showHistory(record)}>{record.name}</Button> : <strong>{record.name}</strong>}{record.jiraKey && <div className="small text-muted">{record.jiraKey}</div>}
                    {['controls', 'requirements'].includes(record.type) && <div className="small text-muted">{controlFramework(record.attributes) || record.standards.join(', ') || 'Unmapped framework'}</div>}
                    {record.type === 'policyTemplates' && <div className="small text-muted">Template catalogue — not a controlled policy</div>}
                    {record.attributes.sourceFamily === 'controlled-policy' && typeof record.attributes.sourcePolicyVersion === 'string' &&
                      <div className="small text-muted">Adoptech controlled source version {record.attributes.sourcePolicyVersion}</div>}
                  </td>
                  {!focused && <td>{recordTypes.find(([value]) => value === record.type)?.[1] ?? record.type}</td>}
                  <td><Badge bg={record.retiredAt || record.status === 'imported' ? 'secondary' : record.status === 'approved' || record.status === 'active' ? 'success' : 'primary'}>{record.status}</Badge>
                    {record.source.system === 'adoptech' && <div className="small text-muted">{focused ? 'Historical source retained' : <>Adoptech · source status: {typeof record.attributes.sourceStatus === 'string' ? record.attributes.sourceStatus : 'not recorded'}<br />Not a new native approval</>}</div>}
                  </td>
                  <td>{record.owner?.name || record.owner?.email || 'Unassigned'}</td>
                  <td>{focused ? imsDate(record.reviewDueOn) : record.reviewDueOn || 'Not set'}</td>
                  <td>{record.recordVersion}</td>
                  <td className="text-nowrap text-end">{focused ? <div className="ims-row-actions">
                    <Button size="sm" variant="outline-secondary" onClick={() => showHistory(record)}>Details</Button>
                    {canManage && !record.retiredAt && <Dropdown align="end"><Dropdown.Toggle size="sm" variant="light" aria-label={`Actions for ${record.name}`}>•••</Dropdown.Toggle><Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                      <Dropdown.Item onClick={() => openEdit(record)}>Edit</Dropdown.Item>
                      <Dropdown.Item onClick={() => openDecision(record, 'review')}>Review</Dropdown.Item>
                      {record.status === 'pending-review' && record.review?.reviewedVersion === record.recordVersion && <Dropdown.Item onClick={() => openDecision(record, 'approval')}>Record decision</Dropdown.Item>}
                      {record.type === 'policies' && record.status === 'approved' && typeof record.attributes.policyBody === 'string' && <Dropdown.Item disabled={saving} onClick={() => publishPolicy(record)}>Publish controlled policy</Dropdown.Item>}
                      <Dropdown.Divider /><Dropdown.Item className="text-danger" onClick={() => retire(record)}>Retire</Dropdown.Item>
                    </Dropdown.Menu></Dropdown>}
                  </div> : <>
                    <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => showHistory(record)}>History</Button>
                    {canManage && record.type === 'policies' && record.status === 'approved' && typeof record.attributes.policyBody === 'string' && <Button size="sm" variant="outline-success" className="me-2" disabled={saving} onClick={() => publishPolicy(record)}>Publish controlled policy</Button>}
                    {canManage && !record.retiredAt && <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => openDecision(record, 'review')}>Review</Button>}
                    {canManage && !record.retiredAt && record.status === 'pending-review' && record.review?.reviewedVersion === record.recordVersion && <Button size="sm" variant="outline-success" className="me-2" onClick={() => openDecision(record, 'approval')}>Record decision</Button>}
                    {canManage && !record.retiredAt && <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(record)}>Edit</Button>}
                    {canManage && !record.retiredAt && <Button size="sm" variant="outline-danger" onClick={() => retire(record)}>Retire</Button>}
                  </>}
                  </td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={focused ? 6 : 7} className="p-4 text-muted">No native operational records match this filter yet.</td></tr>}
            </tbody>
          </Table>
        )}
      </div>
      {(focused || lastPage > 1) && <Card.Footer className="ims-pagination d-flex justify-content-between align-items-center">
        <Button variant="outline-secondary" disabled={loading || page <= 1} onClick={() => setPage(page - 1)}>Previous page</Button>
        <span>Page {page} of {lastPage}{focused ? ` · ${total} matching records` : ''}</span>
        <Button variant="outline-secondary" disabled={loading || page >= lastPage} onClick={() => setPage(page + 1)}>Next page</Button>
      </Card.Footer>}

      <Modal show={editorOpen} onHide={() => setEditorOpen(false)} size="lg" scrollable={focused} dialogClassName={focused ? 'ims-record-modal' : undefined}>
        <Form onSubmit={save}>
          <Modal.Header closeButton><Modal.Title>{editing ? 'Edit controlled record' : 'Create controlled record'}</Modal.Title></Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {editing?.approval && <Alert variant="info">Saving an edit requires a new review and approval. The previous decision remains in history.</Alert>}
            {editing?.status === 'imported' && <Alert variant="info">Editing this imported record creates a native draft. Source facts and historical decisions remain in the source ledger; the import does not constitute a new approval.</Alert>}
            {editing && assuranceTypes.includes(editing.type) && ['verified', 'closed'].includes(editing.status) && <Alert variant="info">Editing a verified or closed assurance record returns the current definition to draft for reassessment. Earlier reported facts and closure remain in version history.</Alert>}
            <Row className="g-3">
              {draft.type === 'risks' && <Col xs={12}><h3 className="h6">Risk assessment and treatment</h3><Row className="g-2">{(['likelihood','consequence','residualLikelihood','residualConsequence'] as const).map((field) => <Col md={3} key={field}><Form.Label>{field}</Form.Label><Form.Control aria-label={field} type="number" min={1} max={5} required value={draft.riskAssessment[field]} onChange={(event) => setDraft({...draft,riskAssessment:{...draft.riskAssessment,[field]:Number(event.target.value)}})} /></Col>)}</Row><p className="small mt-2">Inherent score: {draft.riskAssessment.likelihood*draft.riskAssessment.consequence}; residual score: {draft.riskAssessment.residualLikelihood*draft.riskAssessment.residualConsequence}. Acceptance is a separate recorded decision.</p><Form.Select aria-label="Risk treatment strategy" value={draft.riskAssessment.treatmentStrategy} onChange={(event) => setDraft({...draft,riskAssessment:{...draft.riskAssessment,treatmentStrategy:event.target.value}})}><option value="modify">Modify / reduce</option><option value="retain">Retain</option><option value="avoid">Avoid</option><option value="share">Share / transfer</option></Form.Select><Form.Control className="mt-2" aria-label="Risk treatment explanation" as="textarea" rows={4} maxLength={6000} value={draft.riskAssessment.treatmentExplanation} onChange={(event) => setDraft({...draft,riskAssessment:{...draft.riskAssessment,treatmentExplanation:event.target.value}})} />{editing?.source.system==='adoptech'&&!editing.attributes.riskAssessment&&<Form.Text>Starting ratings are historical source values. Saving creates a native revision that needs review and acceptance.</Form.Text>}</Col>}
              {draft.type === 'policies' && <Col xs={12}>
                <Form.Group className="mb-2"><Form.Label>Document version</Form.Label><Form.Control aria-label="Document version" required pattern="[0-9]+(\.[0-9]+){0,2}" value={draft.documentVersion} onChange={(event) => setDraft({...draft,documentVersion:event.target.value})} /></Form.Group>
                <Form.Group><Form.Label>Controlled policy body</Form.Label><Form.Control aria-label="Controlled policy body" as="textarea" rows={12} maxLength={100000} value={draft.policyBody} onChange={(event) => setDraft({...draft,policyBody:event.target.value})} /><Form.Text>Plain text is rendered safely. Increment the document version for a new revision; edits require a fresh review and approval. The original source PDF remains preserved.</Form.Text></Form.Group>
                {editing?.source.system === 'adoptech' && <Button className="mt-2" size="sm" variant="outline-secondary" disabled={saving} onClick={extractSourceText}>Load text from verified source PDF</Button>}
                {editing?.source.system === 'adoptech' && <p className="small text-muted">Check extracted tables and formatting against the original PDF before approving. Extraction does not claim a new approval.</p>}
              </Col>}
              <Col md={5}>
                <Form.Group><Form.Label>Type</Form.Label><Form.Select aria-label="Record type" value={draft.type} disabled={Boolean(editing) || focused} onChange={(event) => setDraft({ ...draft, type: event.target.value, assurance: emptyAssurance(event.target.value) })}>{recordTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Form.Select></Form.Group>
              </Col>
              <Col md={7}>
                <Form.Group><Form.Label>Name</Form.Label><Form.Control aria-label="Record name" required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group><Form.Label>Status</Form.Label><Form.Select aria-label="Record status" value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>{statuses.map((status) => <option key={status}>{status}</option>)}</Form.Select></Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group><Form.Label>Owner name</Form.Label><Form.Control aria-label="Owner name" value={draft.ownerName} onChange={(event) => setDraft({ ...draft, ownerName: event.target.value })} /></Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group><Form.Label>Owner email</Form.Label><Form.Control aria-label="Owner email" type="email" value={draft.ownerEmail} onChange={(event) => setDraft({ ...draft, ownerEmail: event.target.value })} /></Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group><Form.Label>Effective from</Form.Label><Form.Control aria-label="Effective from" type="date" value={draft.effectiveFrom} onChange={(event) => setDraft({ ...draft, effectiveFrom: event.target.value })} /></Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group><Form.Label>Review due</Form.Label><Form.Control aria-label="Review due" type="date" value={draft.reviewDueOn} onChange={(event) => setDraft({ ...draft, reviewDueOn: event.target.value })} /></Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group><Form.Label>Jira key</Form.Label><Form.Control aria-label="Jira key" placeholder="IMS-000" value={draft.jiraKey} onChange={(event) => setDraft({ ...draft, jiraKey: event.target.value.toUpperCase() })} /></Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group><Form.Label>Record description</Form.Label><Form.Control aria-label="Record description" as="textarea" rows={3} value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></Form.Group>
              </Col>
              {draft.type === 'controlTests' && <>
                <Col xs={12}><Form.Group><Form.Label>Test success criteria</Form.Label><Form.Control aria-label="Test success criteria" required as="textarea" rows={3} value={draft.successCriteria} onChange={(event) => setDraft({ ...draft, successCriteria: event.target.value })} /></Form.Group></Col>
                <Col xs={12}><Form.Group><Form.Label>Test execution mode</Form.Label><Form.Select aria-label="Test execution mode" value={draft.executionMode} onChange={(event) => setDraft({ ...draft, executionMode: event.target.value })}><option value="human">Human / physical execution</option><option value="automated">Automated execution with traceable evidence</option></Form.Select><Form.Text>Scheduling pins these criteria and mode. A bot cannot report a human/physical test as personally performed.</Form.Text></Form.Group></Col>
              </>}
              {['controls', 'requirements'].includes(draft.type) && <>
                <Col xs={12}><Form.Label>Implementation / preparation</Form.Label><Form.Control aria-label="Implementation / preparation" as="textarea" rows={5} maxLength={12000} value={draft.implementation} onChange={(event) => setDraft({ ...draft, implementation: event.target.value })} /></Col>
                <Col xs={12}><Form.Label>Expected evidence (one item per line)</Form.Label><Form.Control aria-label="Expected evidence" as="textarea" rows={3} value={draft.expectedEvidence} onChange={(event) => setDraft({ ...draft, expectedEvidence: event.target.value })} /></Col>
                <Col xs={12}><Form.Label>Reference areas (one per line)</Form.Label><Form.Control aria-label="Reference areas" as="textarea" rows={2} value={draft.requirementRefs} onChange={(event) => setDraft({ ...draft, requirementRefs: event.target.value })} /><Form.Text>Do not assert exact clause coverage until the controlled target edition has been checked.</Form.Text></Col>
              </>}
              {assuranceTypes.includes(draft.type) && <IntegratedManagementAssuranceFields type={draft.type} recordId={editing?.id} value={draft.assurance} onChange={(assurance) => setDraft({ ...draft, assurance })} />}
              <Col xs={12}>
                <Form.Label>Applicable standards</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {standards.map((standard) => <Form.Check id={`ims-standard-${standard.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} key={standard} type="checkbox" label={standard} checked={draft.standards.includes(standard)} onChange={() => toggleStandard(standard)} />)}
                </div>
              </Col>
              <Col xs={12}>
                <div className="d-flex justify-content-between align-items-center mb-2"><Form.Label className="mb-0">Controlled evidence links</Form.Label><Button size="sm" variant="outline-secondary" type="button" onClick={() => setDraft({ ...draft, evidenceLinks: [...draft.evidenceLinks, { url: '', title: '' }] })}>Add link</Button></div>
                {draft.evidenceLinks.map((link, index) => (
                  <Row className="g-2 mb-2" key={index}>
                    <Col md={7}><Form.Control aria-label={`Evidence URL ${index + 1}`} placeholder="https://…" value={link.url} onChange={(event) => updateEvidence(index, 'url', event.target.value)} /></Col>
                    <Col md={4}><Form.Control aria-label={`Evidence title ${index + 1}`} placeholder="Evidence title" value={link.title ?? ''} onChange={(event) => updateEvidence(index, 'title', event.target.value)} /></Col>
                    <Col md={1}><Button aria-label={`Remove evidence link ${index + 1}`} variant="outline-danger" type="button" onClick={() => setDraft({ ...draft, evidenceLinks: draft.evidenceLinks.filter((_, position) => position !== index) })}>×</Button></Col>
                  </Row>
                ))}
                <Form.Text>Only approved HTTPS evidence hosts are accepted. Do not store passwords, tokens or credentials.</Form.Text>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" type="button" onClick={() => setEditorOpen(false)}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save controlled record'}</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(decisionRecord && decisionDraft)} onHide={() => { if (!saving) { setDecisionRecord(null); setDecisionDraft(null); } }} size="lg" scrollable={focused} dialogClassName={focused ? 'ims-record-modal' : undefined}>
        {decisionDraft && <Form onSubmit={saveDecision}>
          <Modal.Header closeButton={!saving}><Modal.Title>{decisionDraft.action === 'review' ? 'Record review' : 'Record approval decision'} · {decisionRecord?.name}</Modal.Title></Modal.Header>
          <Modal.Body>
            {decisionError && <Alert variant="danger">{decisionError}</Alert>}
            <p className="text-muted">This decision applies to version {decisionRecord?.recordVersion}. The acting account and recording time are retained in history.</p>
            <Row className="g-3">
              {decisionDraft.action === 'review' ? <>
                <Col md={6}><Form.Group><Form.Label>Review outcome</Form.Label><Form.Select aria-label="Review outcome" value={decisionDraft.outcome} onChange={(event) => setDecisionDraft({ ...decisionDraft, outcome: event.target.value })}><option value="no-change">Reviewed; no changes required</option><option value="changes-required">Changes required</option></Form.Select></Form.Group></Col>
                <Col md={6}><Form.Group><Form.Label>Next review due</Form.Label><Form.Control aria-label="Next review due" required type="date" value={decisionDraft.nextReviewOn} onChange={(event) => setDecisionDraft({ ...decisionDraft, nextReviewOn: event.target.value })} /></Form.Group></Col>
              </> : <Col xs={12}><Form.Group><Form.Label>Decision</Form.Label><Form.Select aria-label="Approval decision" value={decisionDraft.decision} onChange={(event) => setDecisionDraft({ ...decisionDraft, decision: event.target.value })}><option value="approve">Approve</option>{decisionRecord?.type === 'risks' && <option value="accept-risk">Accept residual risk</option>}<option value="reject">Reject; return for changes</option></Form.Select></Form.Group></Col>}
              <Col xs={12}><Form.Group><Form.Label>Evidence reviewed and decision rationale</Form.Label><Form.Control aria-label="Decision rationale" required as="textarea" rows={4} maxLength={4000} value={decisionDraft.rationale} onChange={(event) => setDecisionDraft({ ...decisionDraft, rationale: event.target.value })} /></Form.Group></Col>
              <Col md={6}><Form.Group><Form.Label>On behalf of (if delegated)</Form.Label><Form.Control aria-label="On behalf of" value={decisionDraft.onBehalfName} onChange={(event) => setDecisionDraft({ ...decisionDraft, onBehalfName: event.target.value })} /></Form.Group></Col>
              <Col md={6}><Form.Group><Form.Label>Delegated person's email</Form.Label><Form.Control aria-label="Delegated person's email" type="email" value={decisionDraft.onBehalfEmail} onChange={(event) => setDecisionDraft({ ...decisionDraft, onBehalfEmail: event.target.value })} /></Form.Group></Col>
              {decisionDraft.onBehalfName.trim() && <Col xs={12}><Form.Group><Form.Label>Delegated authority</Form.Label><Form.Control aria-label="Delegated authority" required as="textarea" rows={2} maxLength={2000} value={decisionDraft.delegationReason} onChange={(event) => setDecisionDraft({ ...decisionDraft, delegationReason: event.target.value })} /></Form.Group></Col>}
            </Row>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" type="button" disabled={saving} onClick={() => { setDecisionRecord(null); setDecisionDraft(null); }}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? 'Recording…' : decisionDraft.action === 'review' ? 'Save review' : 'Save decision'}</Button></Modal.Footer>
        </Form>}
      </Modal>

      <Modal show={Boolean(detail)} onHide={() => setDetail(null)} size="lg" scrollable={focused} dialogClassName={focused ? 'ims-record-modal' : undefined}>
        <Modal.Header closeButton><Modal.Title>{detail?.name} · history</Modal.Title></Modal.Header>
        <Modal.Body className="text-break">
          {error && <Alert variant="danger">{error}</Alert>}
          {focused && detail && <dl className="row small mb-4">
            <dt className="col-4">Owner</dt><dd className="col-8">{detail.owner?.name || detail.owner?.email || 'Unassigned'}</dd>
            <dt className="col-4">Native status</dt><dd className="col-8">{detail.status}</dd>
            <dt className="col-4">Record version</dt><dd className="col-8">{detail.recordVersion}</dd>
            <dt className="col-4">Review due</dt><dd className="col-8">{imsDate(detail.reviewDueOn)}</dd>
            {detail.source.system === 'adoptech' && <><dt className="col-4">Historical source</dt><dd className="col-8">Adoptech · {typeof detail.attributes.sourceStatus === 'string' ? detail.attributes.sourceStatus : 'status not recorded'}. Not a new native approval.</dd></>}
          </dl>}
          {detail?.review && <p><strong>Latest review:</strong> {detail.review.completedOn} · {detail.review.outcome} · {detail.review.rationale}</p>}
          {detail?.approval && <p><strong>Latest decision:</strong> {detail.approval.decision} · reviewed version {detail.approval.reviewedVersion} · {detail.approval.rationale}</p>}
          {detail && <IntegratedManagementAssuranceDetails value={detail.attributes.assurance} />}
          {detail && <IntegratedManagementControlDetails type={detail.type} attributes={detail.attributes} standards={detail.standards} />}
          {detail && !['controls', 'requirements', 'policies', 'risks'].includes(detail.type) && typeof detail.attributes.description === 'string' && <><h3 className="h6">Operational description</h3><p style={{ whiteSpace: 'pre-wrap' }}>{detail.attributes.description}</p></>}
          {detail && Array.isArray(detail.attributes.registerFields) && <>
            <h3 className="h6">Recorded register fields</h3>
            <p className="small text-muted">Source planning, approval or telemetry fields are retained as observations; they are not new native operating evidence, physical-custody confirmation or personal staff attestations.</p>
            <Table responsive size="sm" className="ims-record-fields" aria-label="Recorded register fields" role="table"><tbody>{detail.attributes.registerFields.filter((field): field is { label: string; value: string | number | boolean | null } => Boolean(field) && typeof field === 'object' && typeof field.label === 'string' && (field.value === null || ['string','number','boolean'].includes(typeof field.value))).map((field, index) => <tr key={index} role="row"><th scope="row" role="rowheader">{field.label}</th><td role="cell" style={{ whiteSpace: 'pre-wrap' }}>{field.value === null ? 'Not recorded' : String(field.value)}</td></tr>)}</tbody></Table>
          </>}
          {detail?.type === 'risks' && <><h3 className="h6">{detail.attributes.riskAssessment ? 'Native risk assessment' : 'Historical source risk assessment'}</h3><p>Inherent {riskDraft(detail.attributes).likelihood*riskDraft(detail.attributes).consequence} · residual {riskDraft(detail.attributes).residualLikelihood*riskDraft(detail.attributes).residualConsequence} · {riskDraft(detail.attributes).treatmentStrategy}</p><p>{riskDraft(detail.attributes).treatmentExplanation}</p></>}
          {typeof detail?.attributes.policyBody === 'string' && <><h3 className="h6">Native controlled policy text · document {String(detail.attributes.documentVersion ?? '')}</h3><pre style={{whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>{detail.attributes.policyBody}</pre></>}
          {inspectedVersion?.snapshot && <Alert variant="secondary">
            <h3 className="h6">Immutable recorded version {inspectedVersion.version}</h3>
            <p>{inspectedVersion.snapshot.name} · {inspectedVersion.snapshot.status} · recorded {inspectedVersion.createdAt} by {inspectedVersion.actor}</p>
            <p className="small text-break">Snapshot SHA-256: {inspectedVersion.snapshotSha256}</p>
            <IntegratedManagementAssuranceDetails value={inspectedVersion.snapshot.attributes.assurance} />
            {!!inspectedVersion.snapshot.evidenceLinks?.length && <>
              <h4 className="h6">Evidence references recorded in this version</h4>
              <p className="small">Historical references, not a claim that document bytes or current access have been independently verified.</p>
              <ul>{inspectedVersion.snapshot.evidenceLinks.map((link, index) => <li key={index}>
                {safeEvidenceUrl(link.url) ? <a href={safeEvidenceUrl(link.url)!} target="_blank" rel="noopener noreferrer">{link.title || 'Historical evidence'}{link.version ? ` · v${link.version}` : ''}</a> : 'Historical link withheld: unsafe or unapproved evidence URL'}
                {link.sha256 && <div className="small text-break">Recorded document SHA-256: {link.sha256}</div>}
              </li>)}</ul>
            </>}
            <Button size="sm" variant="outline-secondary" onClick={() => setInspectedVersion(null)}>Close version snapshot</Button>
          </Alert>}
          {sourcePolicyVersions(detail).length > 0 && <>
            <h3 className="h6">Controlled source policy versions</h3>
            <p className="small text-muted">Captured Adoptech content and version chain. These are source versions, not new native reviews or approvals.</p>
            <Table responsive size="sm"><thead><tr><th>Source version</th><th>Source ID</th><th>Clauses</th><th>Content</th></tr></thead>
              <tbody>{sourcePolicyVersions(detail).map((version) => <tr key={version.sourceId}>
                <td>{version.version} · {version.currentSourceVersion ? 'Current source' : 'Historical source'}</td>
                <td>{version.sourceId}</td><td>{version.clauseCount}</td>
                <td>{safeEvidenceUrl(version.evidenceUrl) ? <a href={safeEvidenceUrl(version.evidenceUrl)!} target="_blank" rel="noopener noreferrer">Source content {version.version}</a> : 'No verified content link'}<div className="small text-break">SHA-256: {version.sha256}</div></td>
              </tr>)}</tbody>
            </Table>
          </>}
          {sourceFacts(detail).length > 0 && <>
            <h3 className="h6">Historical source facts</h3>
            <p className="small text-muted">Collections can include unrequested or no-decision states. A dated source decision is not proof of approval of current content and is not a new native decision.</p>
            <Table responsive size="sm"><thead><tr><th>Source collection</th><th>Rows</th><th>Rows with dated source decision</th></tr></thead>
              <tbody>{sourceFacts(detail).map((fact, index) => <tr key={`${fact.kind}:${index}`}><td>{fact.kind}</td><td>{fact.rowCount}</td><td>{fact.rowsWithDecisionTimestamp}</td></tr>)}</tbody>
            </Table>
          </>}
          {detail && detail.evidenceLinks.length > 0 && <>
            <h3 className="h6">Controlled source files and evidence</h3>
            <ul>{detail.evidenceLinks.map((link, index) => <li key={`${link.url}:${index}`}>
              {safeEvidenceUrl(link.url) ? <a href={safeEvidenceUrl(link.url)!} target="_blank" rel="noopener noreferrer">{link.title || 'Controlled evidence'}{link.version ? ` · version ${link.version}` : ''}</a> : 'Unverified evidence URL withheld'}
            </li>)}</ul>
          </>}
          {detailLoading ? <div className="text-center p-4"><Spinner animation="border" /></div> : (
            <Row className="g-4">
              <Col xs={12}>
                <h3 className="h6">Versions</h3>
                <Table responsive size="sm"><thead><tr><th>Version</th><th>Action</th><th>Actor</th><th>Recorded / content</th></tr></thead><tbody>{versions.map((version) => <tr key={version.version}><td>{version.version}</td><td>{version.action}</td><td className="text-break">{version.actor}</td><td>{formatTimestamp(version.createdAt)}{version.snapshot && <div><Button size="sm" variant="outline-secondary" onClick={() => setInspectedVersion(version)}>Inspect version {version.version}</Button></div>}</td></tr>)}</tbody></Table>
              </Col>
              <Col xs={12}>
                <h3 className="h6">Audit events</h3>
                <Table responsive size="sm"><thead><tr><th>Version</th><th>Action</th><th>Changed</th><th>Actor</th></tr></thead><tbody>{events.map((item) => <tr key={item.id}><td>{item.recordVersion}</td><td>{item.action}</td><td className="text-break">{item.metadata.changedFields?.join(', ') || '—'}</td><td className="text-break">{item.actor.name || item.actor.email || item.actor.identifier}</td></tr>)}</tbody></Table>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>
      <Modal show={Boolean(publication)} onHide={() => setPublication(null)}><Modal.Header closeButton><Modal.Title>Controlled policy published</Modal.Title></Modal.Header><Modal.Body>{publication && <><a href={safeEvidenceUrl(publication.url)!} target="_blank" rel="noopener noreferrer">Open verified controlled policy in SharePoint</a><p className="small text-break">SHA-256 {publication.sha256}</p><p>Publication is pinned to the approved native record version. Earlier source files remain unchanged.</p></>}</Modal.Body></Modal>
    </Card>
  );
};

export default IntegratedManagementOperationalRecords;
